import { Options } from 'highcharts';
import { Options as HighstockOptions } from 'highcharts/highstock';
import { merge } from 'lodash';
import { IntlShape } from 'react-intl';
import { getItemTypeConfig } from 'services/items/mappedItemTypes';
import psVHighchartsTheme from 'theme/highcharts-theme';
import { DateIntervalOption, StandardReportInfo } from 'types/reporting';

const DEFAULT_MAX_LIGHTNESS = 90;

export const dateIntervalOptions: DateIntervalOption[] = [
    { value: 'Yr', label: 'Annual' },
    { value: 'Mo', label: 'Monthly' },
];

export const createHSLColorRange = (
    baseHslColor: string,
    steps: number,
    maxLightness: number = DEFAULT_MAX_LIGHTNESS
): string[] => {
    if (steps < 1) {
        throw new Error('Steps must be at least 1');
    }

    if (!baseHslColor.startsWith('hsl(')) {
        throw new Error(`Invalid HSL color format: ${baseHslColor}. Expected format: hsl(h, s%, l%)`);
    }

    const match = baseHslColor.match(/\d+/g);
    if (!match || match.length < 3) {
        throw new Error(`Invalid HSL color format: ${baseHslColor}. Expected format: hsl(h, s%, l%)`);
    }

    const [h, s, l] = match.map(Number);

    if (steps === 1) {
        return [baseHslColor];
    }

    const colorRange: string[] = [];
    const lightnessRange = maxLightness - l;

    for (let i = 0; i < steps; i++) {
        const progress = i / (steps - 1);
        const newLightness = Math.round(l + progress * lightnessRange);
        colorRange.push(`hsl(${h}, ${s}%, ${newLightness}%)`);
    }

    return colorRange;
};

export const getItemTypeColors = (info: StandardReportInfo): Record<number, string[]> => {
    const itemTypeColors: Record<number, string[]> = {};

    for (const itemType of info.itemTypes) {
        const count = info.items.filter((i) => i.itemTypeId === itemType.itemTypeId).length;
        const config = getItemTypeConfig(itemType.itemTypeId);

        if (!config.color || !config.color.hsl) {
            throw new Error(`Missing color configuration for item type ID: ${itemType.itemTypeId}`);
        }

        const maxLightness = config.color?.maxLightness ?? DEFAULT_MAX_LIGHTNESS;
        const colorRange = createHSLColorRange(config.color.hsl, count, maxLightness);
        itemTypeColors[itemType.itemTypeId] = colorRange;
    }
    return itemTypeColors;
};

// since the heatmap does not use highstock options, here is a type for the shared options that can be used in both standard and highstock charts
type CommonChartOptions = Pick<Options, Extract<keyof Options, keyof HighstockOptions>>;

export const getSharedChartOptions = (intl: IntlShape): CommonChartOptions => {
    const options: CommonChartOptions = {
        exporting: {
            enabled: true,
            buttons: {
                contextButton: {
                    menuItems: ['viewFullscreen', 'viewData'],
                    symbol: 'menu',
                },
            },
            csv: {
                dateFormat: "%Y-%m"
            }
        },
        chart: {
            zooming: { type: 'xy', mouseWheel: false },
            panning: {
                enabled: true,
                type: 'y',
            },
            panKey: 'alt',
            resetZoomButton: {
                theme: {
                    fill: 'var(--chakra-colors-background-quaternary)',
                    stroke: 'transparent',
                    r: '4px',
                    style: {
                        color: 'var(--chakra-colors-text-quaternary)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: '500',
                    },
                    states: {
                        hover: {
                            fill: 'var(--chakra-colors-background-quinary)',
                        },
                    },
                },
            },
        },
        lang: {
            noData: intl.formatMessage({ id: 'common_no_data_found' }),
        },
        title: {
            style: {
                fontSize: '1.2em',
            },
        },
        loading: {
            style: {
                backgroundColor: 'var(--chakra-colors-highcharts-loading-background)',
            },
            labelStyle: {
                color: 'var(--chakra-colors-text-primary)',
                fontSize: '16px',
            },
        },
        scrollbar: {
            liveRedraw: false,
        },
        navigator: {
            adaptToUpdatedData: false,
        },
        xAxis: {
            labels: {
                style: {
                    fontSize: '14px',
                },
            },
        },
        yAxis: {
            opposite: false,
            title: {
                style: {
                    fontWeight: '500',
                    fontSize: '16px',
                    fontFamily: 'Poppins',
                },
            },
            stackLabels: {
                enabled: false,
            },
            labels: {
                y: 5,
                style: {
                    fontSize: '14px',
                },
            },
        },
        legend: {
            itemStyle: {
                fontSize: '14px',
                color: 'var(--chakra-colors-text-primary)',
            },
            navigation: {
                activeColor: 'var(--chakra-colors-text-secondary)',
                inactiveColor: 'var(--chakra-colors-text-quaternary)',
                arrowSize: 14,
                style: {
                    color: 'var(--chakra-colors-text-tertiary)',
                    fontSize: '14px',
                },
            },
        },
    };
    return merge({}, psVHighchartsTheme, options);
};
